import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';

import Drawer from '../common/Drawer';
import { Tabs as MuiTabs, Tab } from '@mui/material';
import SaveCancelButtons from '../common/buttons/SaveCancelButtons';
import BusySpinner from '../common/BusySpinner';
import {
	CompanyExternalUser,
	UpdateCompanyExternalUserDTO,
} from '../../data/models/companyExternalUser';
import { AppDispatch, RootState } from '../../data/store';
import { updateCompanyExternalUser } from '../../data/thunks/companyExternalThunk';
import { fetchUserGroups } from '../../data/thunks/userGroupThunk';
import * as GoogleStorageService from '../../services/google-storage.service';
import { showAlert } from '../../data/stores/alertStore';
import { processImageForUpload } from '../../utils/image-processing';
import { FormValues } from './onboarding/external/types';
import { useCrewForm } from './hooks/useCrewForm';
import {
	buildExternalSavePayload,
	createExternalDefaultValues,
	mapExternalUserToFormValues,
} from './onboarding/external/formUtils';
import OverviewStep from './onboarding/external/OverviewStep';
import BasicInformationStep from './onboarding/external/BasicInformationStep';
import ProfessionalDetailsStep from './onboarding/external/ProfessionalDetailsStep';
import WorkLogisticsStep from './onboarding/external/WorkLogisticsStep';
import VehicleTravelStep from './onboarding/external/VehicleTravelStep';
import TravelPreferencesStep from './onboarding/external/TravelPreferencesStep';
import RepresentationStep from './onboarding/external/RepresentationStep';

interface EditExternalCrewModalProps {
	open: boolean;
	handleClose: () => void;
	user?: CompanyExternalUser;
	handleConvert: () => void;
}

export default function EditExternalCrewModal(
	props: EditExternalCrewModalProps
) {
	const { open, user, handleClose, handleConvert } = props;
	const [currentTab, setCurrentTab] = useState(0);
	const [isSaving, setIsSaving] = useState(false);
	const isLoadingGroupsRef = useRef(false);

	const dispatch = useDispatch<AppDispatch>();
	const { userGroups } = useSelector(
		(state: RootState) => state.userGroupState
	);
	const { company } = useSelector((state: RootState) => state.companyState);
	const { astroToken } = useSelector((state: RootState) => state.authState);

	const editExternalDefaultValues = React.useMemo(
		() => createExternalDefaultValues(),
		[]
	);

	const handleEditExternalSave = React.useCallback(
		async (payload: any) => {
			if (!user?.id) {
				return;
			}
			if (!payload || Object.keys(payload).length === 0) {
				return;
			}
			await dispatch(
				updateCompanyExternalUser(
					user.id,
					payload as UpdateCompanyExternalUserDTO
				)
			);
		},
		[dispatch, user?.id]
	);

	const {
		control,
		errors,
		setValue,
		formValues,
		handleSubmit,
		reset,
		hydrateForm,
		saveProgress,
	} = useCrewForm<
		FormValues,
		CompanyExternalUser,
		UpdateCompanyExternalUserDTO,
		UpdateCompanyExternalUserDTO,
		void,
		void
	>({
		defaultValues: editExternalDefaultValues,
		mode: 'onBlur',
		reValidateMode: undefined,
		hydrate: mapExternalUserToFormValues,
		buildSavePayload: (values, helpers) =>
			buildExternalSavePayload(values, helpers.isFieldDirty),
		onSave: handleEditExternalSave,
	});

	const resetForm = (user?: CompanyExternalUser) => {
		if (user) {
			hydrateForm(user);
		} else {
			reset(editExternalDefaultValues);
		}
	};

	// Fetch user groups when component mounts
	useEffect(() => {
		if (
			company?.id &&
			!userGroups.initialized &&
			!isLoadingGroupsRef.current
		) {
			isLoadingGroupsRef.current = true;
			dispatch(
				fetchUserGroups({ companyId: company.id, take: 1000 })
			).finally(() => {
				isLoadingGroupsRef.current = false;
			});
		}
	}, [dispatch, company?.id, userGroups.initialized]);

	useEffect(() => {
		resetForm(user);
	}, [user, open, userGroups.initialized]);

	const handleOnSaveClick = async (data: FormValues) => {
		setIsSaving(true);
		await saveProgress();
		resetForm();
		handleClose();
		setCurrentTab(0);
		setIsSaving(false);
	};

	const handleCancelCloseOnClick = () => {
		resetForm();
		handleClose();
		setCurrentTab(0);
	};

	const handleAirlineChange = (airline: string) => {
		const currentAirlines = formValues.preferredAirlines;
		setValue('preferredAirlines', {
			...currentAirlines,
			[airline]: !currentAirlines[airline],
		});
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setCurrentTab(newValue);
	};

	const handleProfilePhotoDrop = React.useCallback(
		async (acceptedFiles: File[]) => {
			const file = acceptedFiles[0];
			if (!file || !user?.id) return;

			try {
				// Process the image (convert HEIC/HEIF and resize if needed)
				const { processedFile } = await processImageForUpload(file);

				const path = `${company?.id}/profile-pictures/${processedFile.name}`;
				const response = await GoogleStorageService.uploadFile(
					processedFile,
					path,
					astroToken
				);

				// Get a signed GET URL for displaying the uploaded image
				const signedGetUrl = await GoogleStorageService.getSignedGetUrl(
					path,
					astroToken
				);

				// Update the response with the proper public URL
				const updatedResponse = {
					...response,
					publicUrl: signedGetUrl,
				};

				// Update form values immediately so avatar updates
				setValue('profilePictureUrl', updatedResponse);

				// Update external user with new profile picture
				await dispatch(
					updateCompanyExternalUser(user.id, {
						profilePictureUrl: updatedResponse,
					} as UpdateCompanyExternalUserDTO)
				);

				dispatch(
					showAlert({
						message: 'Profile picture uploaded successfully',
						severity: 'success',
					})
				);
			} catch (error) {
				console.error('Error uploading profile picture:', error);
				dispatch(
					showAlert({
						message: 'Error uploading profile picture',
						severity: 'error',
					})
				);
			}
		},
		[user?.id, company?.id, dispatch, astroToken, setValue]
	);

	if (!open || !user) return <></>;

	return (
		<Drawer
			open={open}
			handleClose={handleCancelCloseOnClick}
			title="Edit Crew Member"
			width={600}
		>
			<TabsContainer>
				{isSaving && (
					<BusyOverlay>
						<BusySpinner display="COMPONENT" />
					</BusyOverlay>
				)}
				<Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
					<MuiTabs
						value={currentTab}
						onChange={handleTabChange}
						aria-label="onboarding tabs"
						variant="scrollable"
						scrollButtons="auto"
					>
						<Tab label="Overview" />
						<Tab label="Basic Information" />
						<Tab label="Professional Details" />
						<Tab label="Work Logistics" />
						<Tab label="Vehicle & Travel" />
						<Tab label="Travel Preferences" />
						<Tab label="Representation" />
					</MuiTabs>
				</Box>
				<TabPanel value={currentTab} index={0}>
					<Container>
						<ScrollableContent>
							<OverviewStep
								formValues={formValues}
								setValue={setValue}
								control={control}
								errors={errors}
								user={user}
								userGroups={userGroups.list}
								handleConvert={handleConvert}
								isRequired={false}
								onProfilePhotoDrop={handleProfilePhotoDrop}
							/>
						</ScrollableContent>
						<ButtonContainer>
							<SaveCancelButtons
								handleSave={handleSubmit(handleOnSaveClick)}
								handleCancel={handleCancelCloseOnClick}
								disabled={isSaving}
							/>
						</ButtonContainer>
					</Container>
				</TabPanel>
				<TabPanel value={currentTab} index={1}>
					<Container>
						<ScrollableContent>
							<BasicInformationStep
								formValues={formValues}
								setValue={setValue}
								control={control}
								errors={errors}
								isRequired={false}
							/>
						</ScrollableContent>
						<ButtonContainer>
							<SaveCancelButtons
								handleSave={handleSubmit(handleOnSaveClick)}
								handleCancel={handleCancelCloseOnClick}
								disabled={isSaving}
							/>
						</ButtonContainer>
					</Container>
				</TabPanel>
				<TabPanel value={currentTab} index={2}>
					<Container>
						<ScrollableContent>
							<ProfessionalDetailsStep
								formValues={formValues}
								setValue={setValue}
								control={control}
								errors={errors}
								isRequired={false}
								onDocumentsUpdate={handleEditExternalSave}
							/>
						</ScrollableContent>
						<ButtonContainer>
							<SaveCancelButtons
								handleSave={handleSubmit(handleOnSaveClick)}
								handleCancel={handleCancelCloseOnClick}
								disabled={isSaving}
							/>
						</ButtonContainer>
					</Container>
				</TabPanel>
				<TabPanel value={currentTab} index={3}>
					<Container>
						<ScrollableContent>
							<WorkLogisticsStep
								formValues={formValues}
								setValue={setValue}
								control={control}
								errors={errors}
								isRequired={false}
							/>
						</ScrollableContent>
						<ButtonContainer>
							<SaveCancelButtons
								handleSave={handleSubmit(handleOnSaveClick)}
								handleCancel={handleCancelCloseOnClick}
								disabled={isSaving}
							/>
						</ButtonContainer>
					</Container>
				</TabPanel>
				<TabPanel value={currentTab} index={4}>
					<Container>
						<ScrollableContent>
							<VehicleTravelStep
								formValues={formValues}
								setValue={setValue}
								control={control}
								errors={errors}
								isRequired={false}
							/>
						</ScrollableContent>
						<ButtonContainer>
							<SaveCancelButtons
								handleSave={handleSubmit(handleOnSaveClick)}
								handleCancel={handleCancelCloseOnClick}
								disabled={isSaving}
							/>
						</ButtonContainer>
					</Container>
				</TabPanel>
				<TabPanel value={currentTab} index={5}>
					<Container>
						<ScrollableContent>
							<TravelPreferencesStep
								formValues={formValues}
								setValue={setValue}
								control={control}
								errors={errors}
								isRequired={false}
							/>
						</ScrollableContent>
						<ButtonContainer>
							<SaveCancelButtons
								handleSave={handleSubmit(handleOnSaveClick)}
								handleCancel={handleCancelCloseOnClick}
								disabled={isSaving}
							/>
						</ButtonContainer>
					</Container>
				</TabPanel>
				<TabPanel value={currentTab} index={6}>
					<Container>
						<ScrollableContent>
							<RepresentationStep
								formValues={formValues}
								setValue={setValue}
								control={control}
								errors={errors}
								isRequired={false}
							/>
						</ScrollableContent>
						<ButtonContainer>
							<SaveCancelButtons
								handleSave={handleSubmit(handleOnSaveClick)}
								handleCancel={handleCancelCloseOnClick}
								disabled={isSaving}
							/>
						</ButtonContainer>
					</Container>
				</TabPanel>
			</TabsContainer>
		</Drawer>
	);
}

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			style={{ height: '100%', overflow: 'auto', marginBottom: '20px' }}
			{...other}
		>
			{value === index && <Box sx={{ height: '100%' }}>{children}</Box>}
		</div>
	);
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
`;

const TabsContainer = styled.div`
	height: 100%;
	display: flex;
	flex-direction: column;
	padding-bottom: 20px;
`;

const ScrollableContent = styled.div`
	flex: 1;
	overflow-y: auto;
	padding-right: 8px;
`;

const ButtonContainer = styled.div`
	position: absolute;
	bottom: 16px;
	right: 16px;
`;

const BusyOverlay = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 10;
	display: flex;
	align-items: center;
	justify-content: center;
`;
